import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom } from "jotai/index";
import { isProcessingAtom, volumeAtom } from "~/Atom";
import { useIsAwsChatPage, useIsAwsSettingPage } from "~/utils/envUtils";
import "~/css/RadioButton.css";
import "~/css/ListeningMode.css";
import { PlaySound } from "~/SoundUtils";
import {
  isFirstResponseAtom,
  listeningModeAtom,
  talkingModeAtom,
  waitTimerAtom,
  isCallOutAtom,
  isGreeterAtom,
  styleModeAtom,
  feedbackModeAtom,
} from "~/AtomWithDynamoDB";
import SliderController from "~/components/utils/SliderController";
import { styleModes, feedbackModes } from "~/Commons";

const sessionSettings = {
  minTimer: 2,
  maxTimer: 10,
  decimalPlaces: 0, // 小数点以下の桁数を定数として定義
  percentageMultiplier: 100, // パーセンテージ計算用の定数
};

// eslint-disable-next-line complexity
const ConversationSettings: React.FC = () => {
  const { t } = useTranslation();
  const [isProcessing] = useAtom(isProcessingAtom);
  const [volume] = useAtom(volumeAtom);
  const [talkingMode, setTalkingMode] = useAtom(talkingModeAtom);
  const [isFirstResponse, setIsFirstResponse] = useAtom(isFirstResponseAtom);
  const [listeningMode, setListeningMode] = useAtom(listeningModeAtom);
  const [waitTimer, setWaitTimer] = useAtom(waitTimerAtom);
  const [isCallOut, setIsCallOut] = useAtom(isCallOutAtom);
  const [isGreeter, setIsGreeter] = useAtom(isGreeterAtom);
  const [styleMode, setStyleMode] = useAtom(styleModeAtom);
  const [feedbackMode, setFeedbackMode] = useAtom(feedbackModeAtom);
  const isConfigReadOnly = useIsAwsChatPage();
  const isAwsSettingPage = useIsAwsSettingPage();

  // 裏コマンド用の状態
  const [clickCount, setClickCount] = useState(0);
  const [lastClickTime, setLastClickTime] = useState(0);

  const handleTalkingModeChange = (mode: number) => {
    PlaySound("push2", volume);
    setTalkingMode(mode);
  };

  const handleFirstResponseChange = (isEnabled: boolean) => {
    PlaySound("push2", volume);
    setIsFirstResponse(isEnabled);
  };

  const handleListeningModeChange = (mode: number) => {
    PlaySound("push2", volume);
    setListeningMode(mode);
  };

  const handleCallOutChange = (mode: boolean) => {
    PlaySound("push2", volume);
    setIsCallOut(mode);
  };

  const handleGreeterModeChange = (mode: boolean) => {
    PlaySound("push2", volume);
    setIsGreeter(mode);
  };

  const handleStyleModeChange = (mode: string) => {
    PlaySound("push2", volume);
    setStyleMode(mode);
  };

  const handleFeedbackModeChange = (mode: number) => {
    PlaySound("push2", volume);
    setFeedbackMode(mode);
  };

  const changeTimer = (value: number) => {
    setWaitTimer(value);
  };

  // スライダーの進捗率計算をヘルパー関数化
  const calculateSliderProgress = (value: number): string => {
    return `${
      ((value - sessionSettings.minTimer) / (sessionSettings.maxTimer - sessionSettings.minTimer)) *
      sessionSettings.percentageMultiplier
    }%`;
  };

  // 裏コマンドチェック機能付きの+ボタン処理
  const handlePlusClick = () => {
    // AWS環境かつ設定画面の場合は通常処理
    if (isAwsSettingPage) {
      // 通常の+処理
      changeTimer(Math.min(sessionSettings.maxTimer, waitTimer + 1));
      return;
    }

    // その他(ローカルストレージ登録)の場合は裏コマンドチェック処理
    const currentTime = Date.now();

    // 1秒以内の連続クリックかチェック
    if (currentTime - lastClickTime <= 1000) {
      const newClickCount = waitTimer === 10 ? clickCount + 1 : 1;
      setClickCount(newClickCount);

      // 値が10かつ5回クリックで裏コマンド発動
      if (waitTimer === 10 && newClickCount === 5) {
        setWaitTimer(-999);
        setClickCount(0);
        return;
      }
    } else {
      // 1秒以上間隔が空いたらリセット
      setClickCount(1);
    }

    setLastClickTime(currentTime);

    // 通常の+処理
    changeTimer(Math.min(sessionSettings.maxTimer, waitTimer + 1));
  };

  return (
    <>
      <div className="listening-selector">
        <div className="title font-default-normal-20">{t("styleMode")}</div>
        {/*  ラジオボタンでスタイルモードを切り替え*/}
        <div className="listening-container">
          <form className="listening-list">
            <div className="radio-row">
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("styleModeDescriptionByFormal")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value="1"
                  checked={styleMode === styleModes.formal}
                  name="styleMode"
                  id="formal"
                  onChange={() => handleStyleModeChange(styleModes.formal)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("styleModeByFormal")}</span>
              </label>
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("styleModeDescriptionByCasual")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value="0"
                  checked={styleMode === styleModes.casual}
                  name="styleMode"
                  id="casual"
                  onChange={() => handleStyleModeChange(styleModes.casual)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("styleModeByCasual")}</span>
              </label>
            </div>
          </form>
        </div>
      </div>

      <div className="listening-selector">
        <div className="title font-default-normal-20">{t("listeningMode")}</div>
        {/*  ラジオボタンで3つのモードを切り替え*/}
        <div className="listening-container">
          <form className="listening-list">
            <div className="radio-row">
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("listeningModeDescription0")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value="0"
                  checked={listeningMode === 0}
                  name="listeningMode"
                  id="0"
                  onChange={() => handleListeningModeChange(0)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("listeningMode0")}</span>
              </label>
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("listeningModeDescription1")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value="1"
                  checked={listeningMode === 1}
                  name="listeningMode"
                  id="1"
                  onChange={() => handleListeningModeChange(1)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("listeningMode1")}</span>
              </label>
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("listeningModeDescription2")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value="2"
                  checked={listeningMode === 2}
                  name="listeningMode"
                  id="2"
                  onChange={() => handleListeningModeChange(2)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("listeningMode2")}</span>
              </label>
            </div>
          </form>
        </div>
      </div>

      <div className="listening-selector">
        <div className="title font-default-normal-20">{t("talkingMode")}</div>
        {/*  ラジオボタンで2つの発話モードを切り替え*/}
        <div className="listening-container">
          <form className="listening-list">
            <div className="radio-row">
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("talkingModeDescription0")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value="0"
                  checked={talkingMode === 0}
                  name="talkingMode"
                  id="0"
                  onChange={() => handleTalkingModeChange(0)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("talkingMode0")}</span>
              </label>
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("talkingModeDescription1")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value="1"
                  checked={talkingMode === 1}
                  name="talkingMode"
                  id="1"
                  onChange={() => handleTalkingModeChange(1)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("talkingMode1")}</span>
              </label>
            </div>
          </form>
        </div>
      </div>

      <div className="listening-selector">
        <div className="title font-default-normal-20">{t("firstResponse")}</div>
        {/*  ラジオボタンで一時返答を切り替え*/}
        <div className="listening-container">
          <form className="listening-list">
            <div className="radio-row">
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("firstResponseDescription")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value="true"
                  checked={isFirstResponse === true}
                  name="firstResponse"
                  id="isEnabled"
                  onChange={() => handleFirstResponseChange(true)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("enable")}</span>
              </label>
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("firstResponseDescription")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value="false"
                  checked={isFirstResponse === false}
                  name="firstResponse"
                  id="isDisabled"
                  onChange={() => handleFirstResponseChange(false)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("disable")}</span>
              </label>
            </div>
          </form>
        </div>
      </div>

      <div className="listening-selector">
        <div className="title font-default-normal-20">{t("callOut")}</div>
        {/*  ラジオボタンで呼びかけを切り替え*/}
        <div className="listening-container">
          <form className="listening-list">
            <div className="radio-row">
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("callOutDescription")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value="true"
                  checked={isCallOut === true}
                  name="callOutMode"
                  id="isEnable"
                  onChange={() => handleCallOutChange(true)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("enable")}</span>
              </label>
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("callOutDescription")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value="false"
                  checked={isCallOut === false}
                  name="callOutMode"
                  id="isDisable"
                  onChange={() => handleCallOutChange(false)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("disable")}</span>
              </label>
            </div>
          </form>
        </div>
      </div>

      <div className="listening-selector">
        <div className="title font-default-normal-20">{t("Greeter")}</div>
        {/*  ラジオボタンで独り言を切り替え*/}
        <div className="listening-container">
          <form className="listening-list">
            <div className="radio-row">
              <label className="custom-radio-label" title={t("GreeterDescription")}>
                <input
                  className="custom-radio"
                  type="radio"
                  value="true"
                  checked={isGreeter === true}
                  name="greeter"
                  id="isEnabled"
                  onChange={() => handleGreeterModeChange(true)}
                  disabled={isProcessing}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("enable")}</span>
              </label>
              <label className="custom-radio-label" title={t("GreeterDescription")}>
                <input
                  className="custom-radio"
                  type="radio"
                  value="false"
                  checked={isGreeter === false}
                  name="greeter"
                  id="isDisabled"
                  onChange={() => handleGreeterModeChange(false)}
                  disabled={isProcessing}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("disable")}</span>
              </label>
            </div>
          </form>
        </div>
      </div>

      <div className="listening-selector">
        <div className="title font-default-normal-20">{t("feedbackMode")}</div>
        {/* ラジオボタンでフィードバックモードを切り替え */}
        <div className="listening-container">
          <form className="listening-list">
            <div className="radio-row">
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("feedbackModeDescriptionSimple")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value={feedbackModes.simple}
                  checked={feedbackMode === feedbackModes.simple}
                  name="feedbackMode"
                  id="feedbackSimple"
                  onChange={() => handleFeedbackModeChange(feedbackModes.simple)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("feedbackModeSimple")}</span>
              </label>
              <label
                className={`custom-radio-label ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                title={t("feedbackModeDescriptionDetail")}
              >
                <input
                  className={`custom-radio ${isConfigReadOnly ? "config-read-only" : "config-editable"}`}
                  type="radio"
                  value={feedbackModes.detail}
                  checked={feedbackMode === feedbackModes.detail}
                  name="feedbackMode"
                  id="feedbackDetail"
                  onChange={() => handleFeedbackModeChange(feedbackModes.detail)}
                  disabled={isProcessing}
                  tabIndex={isConfigReadOnly ? -1 : 0}
                  aria-disabled={isConfigReadOnly}
                />
                <span className="radio-custom-circle"></span>
                <span className="view-text font-default-normal-20">{t("feedbackModeDetail")}</span>
              </label>
            </div>
          </form>
        </div>
      </div>

      <div className="slider-container">
        <span className="slider-title font-default-normal-20">{t("session")}</span>
        <div className="slider-content">
          <input
            type="range"
            className="slider-input"
            min={sessionSettings.minTimer}
            max={sessionSettings.maxTimer}
            value={waitTimer}
            step={1}
            style={
              {
                "--value": calculateSliderProgress(waitTimer),
              } as React.CSSProperties
            }
            onChange={(e) => changeTimer(parseInt(e.target.value, 10))}
          />
          <SliderController
            handleMinusValue={() => changeTimer(Math.max(sessionSettings.minTimer, waitTimer - 1))}
            handlePlusValue={handlePlusClick}
          />
          <span className="slider-value">{waitTimer.toFixed(sessionSettings.decimalPlaces)}</span>
        </div>
      </div>
    </>
  );
};

export default ConversationSettings;
